import debounce from "lodash.debounce";
import mailcheck from "mailcheck";
import type { ChangeEvent } from "react";
import { useEffect, useRef, useState } from "react";
import Message from "../Message";
import TextInputDelay from "../TextInputDelay";
import styles from "./InputEmail.module.css";

const domains = ["gmail.com", "hotmail.com", "libra.co.jp"];
const SUGGESTION_DELAY = 900;

interface Props {
  id?: string;
  name?: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  onBlur?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoComplete?: string;
  value: string;
  onChange: (email: string) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

interface State {
  full: string;
  domain: string;
}

export default function InputEmail({ value, onChange, ...props }: Props) {
  const [suggestion, setSuggestion] = useState<State | null>(null);

  function handleSuggestionClick() {
    if (suggestion) {
      onChange(suggestion.full);
      setSuggestion(null);
    }
  }

  const setSuggestionDebounced = useRef(
    debounce(setSuggestion, SUGGESTION_DELAY)
  );

  useEffect(() => {
    mailcheck.run({
      email: value,
      domains,
      suggested(suggestion: State) {
        setSuggestionDebounced.current(suggestion);
      },
      empty() {
        setSuggestionDebounced.current(null);
      },
    });
  }, [value]);

  return (
    <>
      <TextInputDelay
        {...props}
        value={value}
        onValueChange={(value) => onChange(value)}
        type="email"
      />
      {suggestion ? (
        <Message>
          Did you mean{" "}
          <button
            onClick={handleSuggestionClick}
            style={{
              color: "#de2920",
              background: "none",
              appearance: "none" as const,
            }}
            type="button"
            className={styles.InputEmailSuggestionButton}
          >
            {suggestion.domain}
          </button>
          ?
        </Message>
      ) : null}
    </>
  );
}
